<template>
  <div class="list-vouchers-page">
    <PageHeader
      :title="t('myShopData.vouchers.title')"
      summary=""
      :back-link-to="{ name: 'shop.system-objects' }"
      :back-link-text="t('navbar.systemData')"
    />

    <div class="page-content">
      <div class="Vouchers">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :show-reload-button="true"
          data-key="id"
          :enable-grouped-actions="true"
          :exportable="true"
          :show-export-all-button="true"
          :export-config="{ entityType: 'vouchers' }"
          :persistent-filters="persistentFilters"
          @export="handleExport"
        >
          <!-- Composite column: voucher_info (code, name, dates) -->
          <template #cell-voucher_info="{ data }">
            <div class="voucher-info-cell">
              <div class="voucher-row">
                <span
                  v-if="cleanStr(data.name)"
                  class="voucher-name"
                >
                  {{ cleanStr(data.code) }}
                </span>
                <span
                  v-else
                  class="voucher-name voucher-name--empty"
                >
                  {{ t('myShopData.vouchers.noName') }}
                </span>
                <SpmBadge
                  :label="`#${data.voucher_id}`"
                  :colors="{ bg: '#e0f2fe', color: '#0369a1' }"
                  size="sm"
                  variant="soft"
                />
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: lang -->
          <template #cell-lang="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="cleanStr(data.lang)"
                class="lang-badge"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
              <span
                v-else
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <!-- Single column: active -->
          <template #cell-active="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="data.is_active === '1' || data.is_active === 1"
                v-tooltip.top="t('active')"
                class="fas fa-check-circle cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('inactive')"
                class="fas fa-times-circle cell-icon cell-icon--inactive"
              />
            </div>
          </template>
        </spmtableapi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { findPermission } from '@/composables/User';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'VouchersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/vouchers',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'voucher_id',
        header: '',
        fields: [{
          name: 'voucher_id',
          label: t('myShopData.vouchers.headers.id_voucher'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'voucher_info',
        header: t('myShopData.vouchers.headers.name'),
        fields: [{
          name: 'code',
          label: t('myShopData.vouchers.headers.code'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.vouchers.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.vouchers.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.vouchers.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.vouchers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.vouchers.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.vouchers.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { value: '1', label: t('active') },
              { value: '0', label: t('inactive') },
            ],
          },
        }],
      },
    ];

    /**
     * Helper function to clean strings (remove null, undefined, etc.)
     */
    const cleanStr = (value: unknown): string => {
      if (value === null || value === undefined || value === 'null' || value === 'undefined') {
        return '';
      }
      return String(value).trim();
    };

    /**
     * Handle export action
     */
    const handleExport = async () => {
      if (!findPermission('my_data.export')) {
        showToastError('PERMISSION_DENIED');
      }
      // Export logic will be handled by SpmTableAPI
    };

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      handleExport,
      cleanStr,
    };
  },
});
</script>

<style scoped>
/* Voucher info composite column */
.voucher-info-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.voucher-info-cell .voucher-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.voucher-info-cell .voucher-code {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
  background: #f0fdf4;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #86efac;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.025em;
}

.voucher-info-cell .voucher-name {
  font-weight: 500;
  color: #4b5563;
}

.voucher-info-cell .voucher-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.voucher-info-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

/* Language badge */
.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}
</style>

<style scoped lang="scss">
/* Page layout */
.vouchers-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
